import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, ArrowUp, BookOpen, Maximize2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** Floating SOPFlow AI assistant available on every authenticated page. */
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const scroller = useRef(null);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Hide on landing/signin/chat page
  const hidden =
    !user ||
    location.pathname === "/" ||
    location.pathname.startsWith("/signin") ||
    location.pathname.startsWith("/chat");

  useEffect(() => {
    if (open && scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [open, messages, busy]);

  if (hidden) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setErr("");
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg, sources: [] }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/ask", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, sources: data.sources || [] },
      ]);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-trigger"
        className="hidden lg:flex fixed bottom-6 right-6 z-40 items-center gap-2 h-14 pl-5 pr-6 rounded-full btn-primary-glow font-medium text-sm shadow-[0_10px_40px_-10px_rgba(79,70,229,0.6)]"
        aria-label="Open SOPFlow AI"
      >
        <Sparkles size={16} className="text-white" />
        Ask SOPFlow AI
      </button>

      {/* Mobile-friendly minimal FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="lg:hidden fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full btn-primary-glow shadow-[0_10px_40px_-10px_rgba(79,70,229,0.7)] flex items-center justify-center"
        aria-label="Open SOPFlow AI"
        data-testid="chat-widget-trigger-mobile"
      >
        <Sparkles size={20} className="text-white" />
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-3 bottom-3 lg:inset-auto lg:right-6 lg:bottom-24 z-50 lg:w-[400px] max-h-[75vh] flex flex-col glass-strong rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] flex items-center justify-center">
                <Sparkles size={15} className="text-white" />
              </div>
              <div>
                <div className="font-display text-white text-sm">SOPFlow AI</div>
                <div className="text-[10px] text-[#9AA4B2]">Grounded in your SOPs</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/chat");
                }}
                className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-[#9AA4B2] hover:text-white"
                aria-label="Open full page"
                title="Open full page"
                data-testid="chat-widget-expand"
              >
                <Maximize2 size={14} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-[#9AA4B2] hover:text-white"
                aria-label="Close"
                data-testid="chat-widget-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scroller} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[300px]">
            {messages.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-white text-sm font-medium">How can I help?</div>
                <div className="text-[11px] text-[#9AA4B2] mt-1">
                  I only answer using your approved SOPs — with citations.
                </div>
                <div className="mt-4 space-y-1.5">
                  {[
                    "How do I receive a warehouse shipment?",
                    "What is our incident response process?",
                    "How do I submit an expense claim?",
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="w-full text-left glass rounded-2xl px-3 py-2 text-xs text-white hover:bg-white/[0.07] transition"
                    >
                      <span className="text-cyan-300 mr-1.5">→</span>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-[#2563EB]/25 via-[#4F46E5]/20 to-[#06B6D4]/15 border border-white/10 text-white"
                        : "glass text-white"
                    }`}
                  >
                    {m.content}
                    {m.sources?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.sources.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => {
                              setOpen(false);
                              navigate(`/sops/${s.id}`);
                            }}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-cyan-300 hover:text-white"
                          >
                            <BookOpen size={9} /> {s.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            {busy && (
              <div className="flex justify-start">
                <div className="glass rounded-2xl px-3 py-2 flex items-center gap-1.5 text-[11px] text-[#9AA4B2]">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  <span className="ml-1">Thinking…</span>
                </div>
              </div>
            )}
            {err && <div className="text-[11px] text-rose-300">{err}</div>}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="px-3 pb-3 pt-2 border-t border-white/5"
          >
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask about any SOP…"
                rows={1}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2 text-sm text-white placeholder:text-[#5B6472] focus:outline-none focus:border-cyan-400/60 resize-none max-h-24"
                data-testid="chat-widget-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="btn-primary-glow rounded-full w-9 h-9 flex items-center justify-center disabled:opacity-50"
                aria-label="Send"
                data-testid="chat-widget-send"
              >
                <ArrowUp size={14} className="text-white" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
